import { BuildingStructure, LevelModel, PhotoModel } from '../types/protocols';
import { levelDisplayName } from './levelDisplayName';
import { ROOF_PLAN_LABEL, currentRoofPlanBlueprint } from './roofPlan';

/**
 * Drawings a `blueprintLocations/{imageKey}` can point at: a level's drawing
 * or the roof drawing at `structure/roofPlan`. Every place that used to walk
 * `structure.levels[*].blueprints` to resolve an image key goes through here,
 * so a position on the roof drawing renders wherever a level position does.
 */

export type BlueprintOwner =
    | { kind: 'level'; level: LevelModel }
    | { kind: 'roofPlan' };

export interface FoundBlueprint {
    image: PhotoModel;
    /** What the drawing is called: the level's display name or "Takplan". */
    label: string;
    /** The owner's `currentBlueprintKey` is this image. */
    isCurrent: boolean;
    owner: BlueprintOwner;
}

/** One current drawing (levels by number, then the roof drawing), for drawing lists and per-drawing overviews. */
export interface CurrentDrawing {
    imageKey: string;
    image: PhotoModel;
    label: string;
    owner: BlueprintOwner;
    /** Sort key: level number, roof drawing after every level. */
    order: number;
}

const ROOF_PLAN_ORDER = Number.POSITIVE_INFINITY;

/** Levels first (any order), then the roof drawing. Null when no drawing carries the key. */
export const findBlueprintImage = (
    structure: BuildingStructure | undefined,
    imageKey: string
): FoundBlueprint | null => {
    for (const [key, level] of Object.entries(structure?.levels || {})) {
        const image = level?.blueprints?.[imageKey];
        if (image) {
            const model: LevelModel = level.key ? level : { ...level, key };
            return {
                image,
                label: levelDisplayName(model),
                isCurrent: level.currentBlueprintKey === imageKey,
                owner: { kind: 'level', level: model },
            };
        }
    }
    const roofImage = structure?.roofPlan?.blueprints?.[imageKey];
    if (roofImage) {
        return {
            image: roofImage,
            label: ROOF_PLAN_LABEL,
            isCurrent: structure?.roofPlan?.currentBlueprintKey === imageKey,
            owner: { kind: 'roofPlan' },
        };
    }
    return null;
};

/** Every drawing image (current or older) by key, from all levels and the roof drawing. */
export const allBlueprintImages = (structure: BuildingStructure | undefined): Map<string, PhotoModel> => {
    const lookup = new Map<string, PhotoModel>();
    for (const level of Object.values(structure?.levels || {})) {
        for (const [key, image] of Object.entries(level?.blueprints || {})) lookup.set(key, image);
    }
    for (const [key, image] of Object.entries(structure?.roofPlan?.blueprints || {})) lookup.set(key, image);
    return lookup;
};

/** Every drawing that is current for its owner, levels ascending by number and the roof drawing last. */
export const currentDrawings = (structure: BuildingStructure | undefined): CurrentDrawing[] => {
    const drawings: CurrentDrawing[] = [];
    for (const [key, level] of Object.entries(structure?.levels || {})) {
        const imageKey = level?.currentBlueprintKey;
        const image = imageKey ? level.blueprints?.[imageKey] : undefined;
        if (!imageKey || !image) continue;
        const model: LevelModel = level.key ? level : { ...level, key };
        drawings.push({ imageKey, image, label: levelDisplayName(model), owner: { kind: 'level', level: model }, order: model.number });
    }
    drawings.sort((a, b) => a.order - b.order || a.imageKey.localeCompare(b.imageKey));
    const roof = currentRoofPlanBlueprint(structure);
    if (roof) {
        drawings.push({ imageKey: roof.key, image: roof.image, label: ROOF_PLAN_LABEL, owner: { kind: 'roofPlan' }, order: ROOF_PLAN_ORDER });
    }
    return drawings;
};
